import React, { useCallback, useEffect, useRef, useState } from "react";
import { listSessions } from "./core/service";

const PAGE_SIZE = 10;

// Compact "2h ago" / "yesterday" / "Jun 28" relative time for a conversation's list item. Best-effort:
// an unparseable/absent timestamp just renders nothing rather than breaking the row.
export const timeAgo = iso => {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (secs < 60) return "just now";
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return "yesterday";
    if (days < 7) return `${days}d ago`;
    return new Date(then).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// The left sidebar shown when the user opens Chat History: the logged-in user's recent conversations for
// the CURRENT project (most recent first), with "Load more". Clicking an item resumes it via `onSelect`.
// Presentational + self-fetching; scoping (user_id + project_id) is done by the backend `/sessions` query.
const HistoryPanel = ({ userId, projectId, activeSessionId, onSelect }) => {
    const [sessions, setSessions] = useState([]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    // Stale-response guard (the same shape as the widget's guarded effects): every load claims a fresh
    // generation and only the NEWEST may touch state. That covers a slow first page resolving after the
    // response that superseded it, a stale loadMore appending into a list a scope change already replaced,
    // and any response landing after unmount (the cleanup claims the generation).
    const reqGenRef = useRef(0);
    useEffect(
        () => () => {
            reqGenRef.current += 1;
        },
        []
    );

    const load = useCallback(
        async nextOffset => {
            const gen = ++reqGenRef.current;
            setLoading(true);
            setError(false);
            try {
                const data = await listSessions({ userId, projectId, limit: PAGE_SIZE, offset: nextOffset });
                if (gen !== reqGenRef.current) return; // superseded — a newer load owns the state now
                // Treat a non-JSON 200 (a reverse proxy serving the SPA's index.html because /sessions isn't
                // routed here) or an explicit persistence:false the same way — a real error, not a misleading
                // "No past conversations yet." empty state.
                if (!data || typeof data !== "object" || !Array.isArray(data.sessions) || data.persistence === false) {
                    setError(true);
                    return;
                }
                const page = data.sessions;
                setSessions(prev => (nextOffset === 0 ? page : [...prev, ...page]));
                setOffset(nextOffset);
                setHasMore(page.length === PAGE_SIZE); // optimistic: a full page probably has more behind it
            } catch (e) {
                if (gen === reqGenRef.current) setError(true);
            } finally {
                if (gen === reqGenRef.current) setLoading(false);
            }
        },
        [userId, projectId]
    );

    // (Re)load the first page whenever the scope (user/project) changes — e.g. navigating between projects —
    // OR the active conversation changes. The panel stays open BESIDE the chat, so a snapshot from when it
    // was first opened goes stale the moment the user starts a new chat (after Clear, or their first
    // message) or resumes one: `activeSessionId` flips, and this refetch pulls the newest conversation in.
    useEffect(() => {
        load(0);
    }, [load, activeSessionId]);

    const loadMore = () => load(offset + PAGE_SIZE);

    return (
        <div className="fai-history" aria-label="Chat history">
            <div className="fai-history-head">History</div>
            <div className="fai-history-list">
                {sessions.map(s => (
                    <button
                        key={s.session_id}
                        type="button"
                        className={`fai-history-item ${s.session_id === activeSessionId ? "is-active" : ""}`}
                        onClick={() => onSelect(s)}
                        title={s.preview || "Untitled chat"}
                    >
                        <span className="fai-history-preview">{s.preview || "Untitled chat"}</span>
                        <span className="fai-history-meta">
                            {timeAgo(s.updated_at)}
                            {s.turn_count ? ` · ${s.turn_count} msg${s.turn_count === 1 ? "" : "s"}` : ""}
                        </span>
                    </button>
                ))}
                {loading && <div className="fai-history-note">Loading…</div>}
                {!loading && error && <div className="fai-history-note fai-history-error">Couldn’t load history.</div>}
                {!loading && !error && sessions.length === 0 && (
                    <div className="fai-history-note">No past conversations yet.</div>
                )}
                {!loading && hasMore && (
                    <button type="button" className="fai-history-more" onClick={loadMore}>
                        Load more
                    </button>
                )}
            </div>
        </div>
    );
};

export default HistoryPanel;
